import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import MetricCard from '../../common_components/MetricCard';
import { formatStat } from '../../helper/statsFormatter';
import EmailChannelTable from './EmailChannelTable';
import ChannelTabs from '../../common_components/ChannelTabs';
const TrendBadge = ({ trend, value }) => (_jsxs(Box, { sx: {
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "16px",
        fontWeight: 600,
        bgcolor: trend === "up" ? "#ECFDF3" : "#FEF3F2",
        color: trend === "up" ? "#027A48" : "#B42318",
        whiteSpace: "nowrap"
    }, className: 'all-Font-Sizes-12', children: [value, _jsx("img", { src: trend === "up"
                ? "/assets/trend-up.svg"
                : "/assets/trending-down.svg", alt: trend === "up" ? "Trend Up" : "Trend Down", width: 12, height: 12, style: { marginLeft: 4 } })] }));
const BenchmarkCard = ({ months, openRate, openTrend, openTrendValue, clickRate, clickTrend, clickTrendValue, showOpenRate = true, }) => {
    return (_jsxs(Paper, { elevation: 0, sx: {
            flex: "1 1 0",
            bgcolor: "#EFF8FF", // softer neutral background
            border: "1px solid #E4E7EC",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column"
        }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 1,
                    py: 1,
                    borderBottom: "1px solid #E4E7EC"
                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Box, { sx: {
                                    width: 32,
                                    height: 32,
                                    bgcolor: "#E0EAFF",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }, children: _jsx("img", { src: "/assets/benchmarking.svg", alt: "Benchmark", width: 18, height: 18 }) }), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: '13px !important', fontWeight: 600, color: "#101828" }, children: "Benchmark" })] }), _jsxs(Box, { sx: {
                            bgcolor: "#E0EAFF",
                            color: "#1D4ED8",
                            fontWeight: 600,
                            fontSize: "12px",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "20px"
                        }, children: [months, " Month(s)"] })] }), _jsxs(Box, { sx: { display: "flex", gap: 1, p: 1 }, children: [showOpenRate && openRate && openTrend && openTrendValue && (_jsxs(Paper, { elevation: 0, sx: {
                            flex: 1,
                            p: 1,
                            borderRadius: "8px",
                            bgcolor: "#FFFFFF",
                            border: "1px solid #EAECF0"
                        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, color: "#667085" }, children: [_jsx("img", { src: "/assets/openrate.svg", alt: "Open Rate", width: 14, height: 14 }), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: '13px !important', fontWeight: 500 }, children: "Open Rate" })] }), _jsxs(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "baseline" }, children: [_jsx(Typography, { className: "head-Font-Sizes", sx: { fontSize: '14px !important', fontWeight: 700, color: "#101828", mr: 0.5 }, children: openRate }), _jsx(Typography, { sx: { fontSize: "12px", color: "#667085", fontWeight: 600 }, children: "%" })] }), _jsx(TrendBadge, { trend: openTrend, value: openTrendValue })] })] })), _jsxs(Paper, { elevation: 0, sx: {
                            flex: 1,
                            p: 1.5,
                            borderRadius: "8px",
                            bgcolor: "#FFFFFF",
                            border: "1px solid #EAECF0"
                        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, color: "#667085" }, children: [_jsx("img", { src: "/assets/stats/cpc.svg", alt: "Click Rate", width: 14, height: 14 }), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: '13px !important', fontWeight: 500 }, children: "Click Rate" })] }), _jsxs(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "baseline" }, children: [_jsx(Typography, { className: "head-Font-Sizes", sx: { fontSize: '14px !important', fontWeight: 700, color: "#101828", mr: 0.5 }, children: clickRate }), _jsx(Typography, { sx: { fontSize: "12px", color: "#667085", fontWeight: 600 }, children: "%" })] }), _jsx(TrendBadge, { trend: clickTrend, value: clickTrendValue })] })] })] })] }));
};
const ChannelPerformance = ({ reportData, currentDateRange, previousDateRange, clientId, activeTabKey, setActiveTabKey }) => {
    // ✅ Determine which tabs are available based on data
    const isCompareOn = !!previousDateRange;
    const availableTabs = useMemo(() => {
        const tabs = [];
        // A tab is shown whenever the backend sent that channel — even if the current range
        // has no rows (metrics show 0 and any comparison data is still viewable).
        if (reportData) {
            tabs.push({ key: 'campaigns', label: 'Campaigns' });
            tabs.push({ key: 'flows', label: 'Flows' });
            tabs.push({ key: 'sms', label: 'SMS/MMS' });
        }
        return tabs;
    }, [reportData]);
    useEffect(() => {
        // if tabs changed and current key not present, fallback
        if (availableTabs.length > 0 && !availableTabs.some(t => t.key === activeTabKey)) {
            setActiveTabKey(availableTabs[0]?.key || "");
        }
    }, [availableTabs, activeTabKey, setActiveTabKey]);
    const currentTabKey = activeTabKey;
    // ✅ Trend helper
    const getTrend = (diff) => {
        if (isNaN(diff))
            return { dir: "neutral", text: "0%" };
        if (diff > 0)
            return { dir: "up", text: `${Math.abs(diff).toFixed(0)}%` };
        if (diff < 0)
            return { dir: "down", text: `${Math.abs(diff).toFixed(0)}%` };
        return { dir: "neutral", text: "0%" };
    };
    // ✅ Get channel data based on current tab
    const getChannelData = () => {
        if (currentTabKey === 'campaigns') {
            return {
                stats: reportData?.campaigns?.stats,
                benchmark: reportData?.campaigns?.benchmark,
                table: reportData?.campaigns?.table,
                hasBenchmark: !!reportData?.campaigns?.benchmark,
                hasStats: true,
                itemLabel: "No. Of Emails",
                title: "Campaign"
            };
        }
        else if (currentTabKey === 'flows') {
            return {
                stats: reportData?.flows?.stats,
                benchmark: null,
                table: reportData?.flows?.table,
                hasBenchmark: false,
                hasStats: false,
                itemLabel: "No. Of Flows",
                title: "Flow"
            };
        }
        else if (currentTabKey === 'sms') {
            return {
                stats: reportData?.sms?.stats,
                benchmark: reportData?.sms?.benchmark,
                table: reportData?.sms?.table,
                hasBenchmark: !!reportData?.sms?.benchmark,
                hasStats: true,
                itemLabel: "No. Of SMS",
                title: "SMS/MMS"
            };
        }
        return null;
    };
    const channelData = getChannelData();
    // ✅ Extract stats from pre-computed stats array
    const extractStat = (statsArray, key) => {
        if (!statsArray || !Array.isArray(statsArray))
            return null;
        const found = statsArray.find((s) => s[key]);
        return found ? found[key] : null;
    };
    // ✅ Build metrics data (only for campaigns/sms)
    const getMetrics = () => {
        if (!channelData || !channelData.hasStats || !channelData.stats)
            return null;
        return {
            promoRecipients: extractStat(channelData.stats, 'promoRecipients'),
            nonPromoRecipients: extractStat(channelData.stats, 'nonPromoRecipients'),
            totalRecipients: extractStat(channelData.stats, 'totalRecipients'),
            openRate: extractStat(channelData.stats, 'openRate'),
            clickRate: extractStat(channelData.stats, 'clickRate'),
            itemCount: extractStat(channelData.stats, 'noOfEmails') || extractStat(channelData.stats, 'noOfSms'),
            noOfUnsubscribers: extractStat(channelData.stats, 'noOfUnsubscribers')
        };
    };
    const metrics = getMetrics();
    // ✅ Build benchmark data (only for campaigns/sms)
    const getBenchmarks = () => {
        if (!channelData?.hasBenchmark || !channelData.benchmark)
            return null;
        const benchmark = channelData.benchmark;
        const isSms = currentTabKey === 'sms';
        const buildBenchmark = (key, months) => {
            const data = benchmark[key];
            if (!data)
                return null;
            const openRateData = data.openRate;
            const clickRateData = data.clickRate;
            return {
                months,
                openRate: openRateData ? formatStat(openRateData.rate, 'open_rate').replace('%', '') : undefined,
                openTrend: openRateData ? (openRateData.diff >= 0 ? "up" : "down") : undefined,
                openTrendValue: openRateData ? formatStat(openRateData.diff, 'open_rate') : undefined,
                clickRate: formatStat(clickRateData?.rate, 'click_rate').replace('%', ''),
                clickTrend: (clickRateData?.diff >= 0 ? "up" : "down"),
                clickTrendValue: formatStat(clickRateData?.diff, 'click_rate'),
                showOpenRate: !isSms && !!openRateData
            };
        };
        return [
            buildBenchmark('2months', 2),
            buildBenchmark('6months', 6),
            buildBenchmark('12months', 12)
        ].filter(Boolean);
    };
    const benchmarks = getBenchmarks();
    // ✅ No tabs available
    if (availableTabs.length === 0) {
        return (_jsx(Paper, { elevation: 0, sx: { border: "1px solid #EAECF0", borderRadius: "8px", overflow: "hidden", mt: 3, mb: 3, p: 4, textAlign: 'center' }, children: _jsx(Typography, { sx: { color: '#667085' }, children: "No channel data available." }) }));
    }
    return (_jsxs(Paper, { elevation: 0, sx: {
            border: "1px solid #EAECF0",
            borderRadius: "8px",
            overflow: "hidden",
            mb: 3
        }, children: [_jsx(ChannelTabs, { tabs: availableTabs, value: activeTabKey, onChange: setActiveTabKey }), _jsxs(Box, { sx: { p: 1, bgcolor: "#F9FAFB", minHeight: "200px" }, children: [currentTabKey === "flows" && channelData?.table && (_jsx(EmailChannelTable, { channelKey: "flows", title: "Flows", tableData: channelData.table, currentDateRange: currentDateRange, previousDateRange: previousDateRange, clientId: clientId })), currentTabKey !== "flows" && (_jsx(_Fragment, { children: !metrics ? (_jsx(Box, { sx: {
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                pt: 4,
                                pb: 4
                            }, children: _jsx(Typography, { sx: { color: '#667085' }, children: "No data available for this channel." }) })) : (_jsxs(_Fragment, { children: [benchmarks && benchmarks.length > 0 && (_jsx(Box, { sx: { display: "flex", gap: 1, mb: 1 }, children: benchmarks.map((bm, index) => (_jsx(BenchmarkCard, { ...bm }, index))) })), _jsxs(Box, { sx: {
                                        display: "grid",
                                        gridTemplateColumns: "repeat(4, 1fr)",
                                        gap: 1,
                                        mb: 2
                                    }, children: [_jsx(MetricCard, { title: "Promo Recipients", tooltipText: "This metric is calculated based on the\nword 'PROMO' or percentage numbers\nin the campaign name.", icon: _jsx("img", { src: "/assets/promo.svg", alt: "Promo", width: 14, height: 14 }), value: formatStat(metrics.promoRecipients?.curr, 'recipients'), comparisonValue: isCompareOn ? formatStat(metrics.promoRecipients?.prev, 'recipients') : undefined, percentageText: isCompareOn ? getTrend(metrics.promoRecipients?.diff).text : undefined, trend: isCompareOn ? getTrend(metrics.promoRecipients?.diff).dir : undefined }), _jsx(MetricCard, { title: "Non Promo Recipients", tooltipText: "This metric is calculated by excluding\npromo recipients.", icon: _jsx("img", { src: "/assets/nonpromo.svg", alt: "Non Promo", width: 14, height: 14 }), value: formatStat(metrics.nonPromoRecipients?.curr, 'recipients'), comparisonValue: isCompareOn ? formatStat(metrics.nonPromoRecipients?.prev, 'recipients') : undefined, percentageText: isCompareOn ? getTrend(metrics.nonPromoRecipients?.diff).text : undefined, trend: isCompareOn ? getTrend(metrics.nonPromoRecipients?.diff).dir : undefined }), _jsx(MetricCard, { title: "Total Recipients", tooltipText: "This metric is calculated as the sum\nof promo and non-promo recipients.", icon: _jsx("img", { src: "/assets/totalrec.svg", alt: "Total Rec", width: 14, height: 14 }), value: formatStat(metrics.totalRecipients?.curr, 'recipients'), comparisonValue: isCompareOn ? formatStat(metrics.totalRecipients?.prev, 'recipients') : undefined, percentageText: isCompareOn ? getTrend(metrics.totalRecipients?.diff).text : undefined, trend: isCompareOn ? getTrend(metrics.totalRecipients?.diff).dir : undefined }), currentTabKey !== "sms" && (_jsx(MetricCard, { title: "Open Rate", tooltipText: "(Unique Opened Emails / Total Delivered Emails) * 100", icon: _jsx("img", { src: "/assets/openrate.svg", alt: "Open Rate", width: 14, height: 14 }), value: formatStat(metrics.openRate?.curr, 'open_rate'), comparisonValue: isCompareOn ? formatStat(metrics.openRate?.prev, 'open_rate') : undefined, percentageText: isCompareOn ? getTrend(metrics.openRate?.diff).text : undefined, trend: isCompareOn ? getTrend(metrics.openRate?.diff).dir : undefined })), _jsx(MetricCard, { title: "Click Rate", tooltipText: "(Unique Clicked Emails / Total Delivered Emails) * 100", icon: _jsx("img", { src: "/assets/stats/cpc.svg", alt: "Click Rate", width: 14, height: 14 }), value: formatStat(metrics.clickRate?.curr, 'click_rate'), comparisonValue: isCompareOn ? formatStat(metrics.clickRate?.prev, 'click_rate') : undefined, percentageText: isCompareOn ? getTrend(metrics.clickRate?.diff).text : undefined, trend: isCompareOn ? getTrend(metrics.clickRate?.diff).dir : undefined }), _jsx(MetricCard, { title: channelData?.itemLabel || "No. Of Items", tooltipText: "Count of emails that were sent in the selected date range.", icon: _jsx("img", { src: "/assets/mail.svg", alt: "Mail", width: 14, height: 14 }), value: formatStat(metrics.itemCount?.curr, 'num_emails'), comparisonValue: isCompareOn ? formatStat(metrics.itemCount?.prev, 'num_emails') : undefined, percentageText: isCompareOn ? getTrend(metrics.itemCount?.diff).text : undefined, trend: isCompareOn ? getTrend(metrics.itemCount?.diff).dir : undefined }), _jsx(MetricCard, { title: "No. Of Unsubscribers", tooltipText: "Unique unsubscribed.", icon: _jsx("img", { src: "/assets/unsubscriber.svg", alt: "Unsub", width: 14, height: 14 }), value: formatStat(metrics.noOfUnsubscribers?.curr, 'recipients'), comparisonValue: isCompareOn ? formatStat(metrics.noOfUnsubscribers?.prev, 'recipients') : undefined, percentageText: isCompareOn ? getTrend(metrics.noOfUnsubscribers?.diff).text : undefined, trend: isCompareOn ? getTrend(metrics.noOfUnsubscribers?.diff).dir : undefined })] }), channelData?.table && (_jsx(EmailChannelTable, { channelKey: currentTabKey, title: channelData.title, tableData: channelData.table, currentDateRange: currentDateRange, previousDateRange: previousDateRange, clientId: clientId }))] })) }))] })] }));
};
export default ChannelPerformance;
